import { fetchPublicStats, PUBLIC_STATS_ENDPOINT } from "./endpoint.js";
import { PUBLIC_STATS_SNAPSHOT } from "./snapshot.js";
/**
 * One in-flight request per endpoint, held for the life of the module. Keyed by
 * endpoint rather than kept as a single promise so an app pointing at more than
 * one API base still gets the answer it asked for instead of the first caller's.
 */
const inFlight = new Map();
/**
 * How long to keep trying before publishing the committed snapshot instead.
 *
 * The caller that matters is an Astro build: the first page's frontmatter makes
 * the one request, every later page reads its answer, and the result is baked
 * into static HTML for a deploy's lifetime. A single transient blip therefore
 * costs a whole site's worth of pages — one refused connection and 499 pages
 * publish last week's figure — which is far too much to pay for not asking
 * again. Three attempts across a second and a quarter is cheap against that,
 * and it is bounded so a genuine outage cannot hold a build open: the timeout
 * in `endpoint.ts` caps each attempt, so the worst case is a fixed few seconds
 * once per process, not per page.
 *
 * Retrying INSIDE the one cached promise is what keeps every caller on the same
 * answer. Retrying per caller would let page 1 publish the snapshot and page 2
 * the live count, which is the two-answers-on-one-site bug this package exists
 * to end.
 */
const RETRY_DELAYS_MS = [250, 1_000];
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const describe = (error) => (error instanceof Error ? error.message : String(error));
/**
 * Read the live counts, or say out loud that we are publishing the snapshot.
 *
 * The warning is the point of this function existing separately: a page that
 * quietly falls back looks exactly like a page that succeeded, so an endpoint
 * that has been failing for a week produces a build log that says nothing and a
 * site that over-claims coverage in silence.
 */
async function readOrFallBack(endpoint) {
    for (let attempt = 0;; attempt += 1) {
        try {
            return await fetchPublicStats(endpoint);
        }
        catch (error) {
            if (attempt >= RETRY_DELAYS_MS.length) {
                const { captured_at: capturedAt, stats } = PUBLIC_STATS_SNAPSHOT;
                console.warn(`public stats: ${endpoint} did not answer in ${RETRY_DELAYS_MS.length + 1} attempts — ` +
                    `publishing the committed snapshot (${stats.data_brokers} brokers, ${stats.custom_sites} custom sites, ` +
                    `captured ${capturedAt}). Last error: ${describe(error)}`);
                return stats;
            }
            await wait(RETRY_DELAYS_MS[attempt]);
        }
    }
}
/**
 * The live counts, falling back to the committed snapshot when the endpoint
 * cannot answer. Never rejects: every caller is a render site, and a page that
 * shows last week's exact count is right where a page that throws is not.
 *
 * N callers produce ONE request. Components call this freely on mount — the
 * promise is cached, so the count on the pricing table and the count in the
 * hero are the same fetch and land together.
 */
export function loadPublicStats(endpoint = PUBLIC_STATS_ENDPOINT) {
    let pending = inFlight.get(endpoint);
    if (!pending) {
        pending = readOrFallBack(endpoint);
        inFlight.set(endpoint, pending);
    }
    return pending;
}
/**
 * Whether these counts were read from the endpoint.
 *
 * `loadPublicStats` never rejects: it answers with the committed snapshot
 * OBJECT when the endpoint could not be read, so the object's identity IS the
 * provenance and there is no second record of one load to fall out of step with
 * it.
 *
 * What needs to ask is a surface that promises an author a live figure. The
 * editor's macro picker shows the number each macro publishes; a number that
 * was captured weeks ago is not that number unless the picker says so, and a
 * silent fallback there is the confusion the macros exist to end.
 */
export function statsAreLive(stats) {
    return stats !== PUBLIC_STATS_SNAPSHOT.stats;
}
/** Drops the cached request. For tests — production loads once per process. */
export function resetPublicStatsCache() {
    inFlight.clear();
}
